import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { leaveService } from '../services/leaveService';
import EditLeaveBalanceDialog from './EditLeaveBalanceDialog';
import './LeaveBalance.css';

const LeaveBalance = ({ employeeId }) => {
  const { hasModulePermission } = useAuth();
  const canUpdate = hasModulePermission('EMPLOYEES', 'update');
  const [balance, setBalance] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showEditDialog, setShowEditDialog] = useState(false);

  const loadBalance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await leaveService.getBalance(employeeId);
      setBalance(response.data);
    } catch (err) {
      setError('Erreur lors du chargement du solde');
      console.error('Error loading balance:', err);
    } finally {
      setLoading(false);
    }
  }, [employeeId]);

  useEffect(() => {
    if (employeeId) {
      loadBalance();
    }
  }, [employeeId, loadBalance]);

  if (loading) {
    return <div className="loading">Chargement du solde...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!balance) {
    return null;
  }

  // Calculer les valeurs à afficher
  const previousYearBalance = balance.previousYearBalance || 0;
  const currentYearTheoretical = balance.calculatedDays != null ? balance.calculatedDays : balance.totalDays;
  const acquiredDays = balance.acquiredDays != null ? balance.acquiredDays : 0;
  const totalBalance = previousYearBalance + currentYearTheoretical;
  const acquiredPlusPrevious = previousYearBalance + acquiredDays;
  const currentActualBalance = acquiredPlusPrevious - (balance.usedDays || 0);
  const theoreticalRemaining = totalBalance - (balance.usedDays || 0);
  
  const percentage = totalBalance > 0 
    ? (balance.usedDays / totalBalance) * 100 
    : 0;

  const handleEditSuccess = () => {
    setShowEditDialog(false);
    loadBalance(); // Recharger le solde après modification
  };

  return (
    <div className="leave-balance">
      <div className="leave-balance-header">
        <h4>Solde de congés - {balance.employeeName}</h4>
        {canUpdate && (
          <button 
            className="btn btn-sm btn-outline" 
            onClick={() => setShowEditDialog(true)}
          >
            Modifier
          </button>
        )}
      </div>
      <div className="balance-grid">
        <div className="balance-card">
          <div className="balance-label">Solde de l'année précédente</div>
          <div className={`balance-value ${previousYearBalance < 0 ? 'negative' : ''}`}>
            {previousYearBalance} jours
          </div>
        </div>
        <div className="balance-card">
          <div className="balance-label">Jours acquis depuis début d'année</div>
          <div className="balance-value">{acquiredDays} jours</div>
        </div>
        <div className="balance-card">
          <div className="balance-label">Solde (année précédente + acquis)</div>
          <div className={`balance-value ${acquiredPlusPrevious < 0 ? 'negative' : 'remaining'}`}>
            {acquiredPlusPrevious} jours
          </div>
        </div>
        <div className="balance-card">
          <div className="balance-label">Solde de l'année actuelle théorique</div>
          <div className="balance-value">{currentYearTheoretical} jours</div>
        </div>
        <div className="balance-card highlight">
          <div className="balance-label">Total (année précédente + année actuelle)</div>
          <div className={`balance-value ${totalBalance < 0 ? 'negative' : 'remaining'}`}>
            {totalBalance} jours
          </div>
        </div>
        <div className="balance-card">
          <div className="balance-label">Utilisés</div>
          <div className="balance-value used">{balance.usedDays} jours</div>
        </div>
        <div className="balance-card">
          <div className="balance-label">En attente</div>
          <div className="balance-value pending">{balance.pendingDays} jours</div>
        </div>
      </div>
      <div className="balance-card">
          <div className="balance-label">Solde actuelle</div>
          <div className={`balance-value ${currentActualBalance < 0 ? 'negative' : 'remaining'}`}>
            {currentActualBalance} jours
          </div>
        </div>
        <div className="balance-card">
          <div className="balance-label">Solde théorique restant</div>
          <div className={`balance-value ${theoreticalRemaining < 0 ? 'negative' : 'remaining'}`}>
            {theoreticalRemaining} jours
          </div>
        </div>
      <div className="progress-bar">
        <div 
          className="progress-fill" 
          style={{ width: `${percentage}%` }}
        />
      </div>
      <div className="progress-text">
        {percentage.toFixed(1)}% utilisés
      </div>
      {showEditDialog && (
        <EditLeaveBalanceDialog
          employeeId={employeeId}
          employeeName={balance.employeeName}
          currentAdjustment={balance.manualAdjustment || 0}
          calculatedDays={balance.calculatedDays || balance.totalDays}
          currentRemaining={totalBalance - balance.usedDays - balance.pendingDays}
          onClose={() => setShowEditDialog(false)}
          onSuccess={handleEditSuccess}
        />
      )}
    </div>
  );
};

export default LeaveBalance;

